import { useEffect, useState, useMemo } from "react";
import { useAuth } from "../hooks/useAuth";
import * as packService from "../services/investmentPackService";
import type { InvestmentPack, Payment } from "../types/api";
import { Button } from "../components/ui/button";
import { useToast } from "../components/ui/use-toast";
import { Skeleton } from "../components/ui/skeleton";
import { useTranslation } from "react-i18next";
import {
  pickBenefitText,
  pickPackDescription,
  pickPackName,
} from "../utils/localeDisplay";
import PaymentFormModal, {
  type PaymentFormData,
} from "../components/PaymentFormModal";
import * as paymentService from "../services/paymentService";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import StripePaymentModal from "../components/StripePaymentModal";
import { Copy } from "lucide-react";
import { Pagination } from "../components/table/DataTable";

const InvestmentPacksPage = () => {
  const { user, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [packs, setPacks] = useState<InvestmentPack[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPack, setSelectedPack] = useState<InvestmentPack | null>(null);
  const [paymentFormOpen, setPaymentFormOpen] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [pendingPayment, setPendingPayment] = useState<Payment | null>(null);
  const [stripePayment, setStripePayment] = useState<Payment | null>(null);
  const [confirmationValue, setConfirmationValue] = useState("");
  const [confirming, setConfirming] = useState(false);
  const getPaymentErrorMessage = (error: unknown) => {
    if (typeof error === "string") {
      const lowered = error.toLowerCase();
      if (lowered.includes("wallet crypto") || lowered.includes("adresse de réception")) {
        return t(
          "payment.form.cryptoWalletMissing",
          "L'adresse du réseau choisi n'est pas configurée."
        );
      }
      return error;
    }
    if (error instanceof Error) {
      return error.message;
    }
    return t("investmentPacks.purchaseErrorDescription");
  };

  const [page, setPage] = useState(1);
  const [totalPacks, setTotalPacks] = useState(0);
  const packsPerPage = 12;
  const totalPages = Math.max(1, Math.ceil(totalPacks / packsPerPage));

  const fetchPacks = async () => {
    try {
      setIsLoading(true);
      const offset = (page - 1) * packsPerPage;
      const data = await packService.listPacks({ 
        limit: packsPerPage, 
        offset 
      });
      setPacks(data);
      // TODO: Le backend devrait retourner le total pour la pagination
      // Pour l'instant, on suppose qu'il y a plus de packs si on reçoit exactement packsPerPage
      if (data.length === packsPerPage) {
        setTotalPacks(page * packsPerPage + 1); // Estimation
      } else {
        setTotalPacks((page - 1) * packsPerPage + data.length);
      }
    } catch (err) {
      console.error("Erreur lors du chargement des packs:", err);
      toast({
        title: t("investmentPacks.errorTitle"),
        description:
          (err as string) ?? t("investmentPacks.errorLoadDescription"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void fetchPacks();
  }, [toast, t, page]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const handleOpenPurchase = (pack: InvestmentPack) => {
    if (!isAuthenticated) {
      // Rediriger vers la page de connexion avant achat
      toast({
        title: t("auth.loginRequiredTitle", "Connexion requise"),
        description: t(
          "auth.loginRequiredDescription",
          "Vous devez être connecté pour acheter un pack d'investissement."
        ),
        variant: "destructive",
      });
      navigate("/login", { state: { from: "/investment-packs" } });
      return;
    }

    setSelectedPack(pack);
    setPaymentFormOpen(true);
  };

  const handlePaymentConfirm = async (paymentData: PaymentFormData) => {
    if (!selectedPack || !user) return;

    setPurchasing(true);
    try {
      // Les informations de carte sont gérées via Stripe au prochain écran

      // Créer l'achat du pack (toujours en "pending" maintenant)
      const purchase = await packService.purchasePack(selectedPack.id);

      // Créer un paiement avec les données du formulaire
      const paymentCurrency =
        paymentData.method === "crypto"
          ? paymentData.cryptoAsset ?? "USDT"
          : "USD";
      const payment = await paymentService.createPayment({
        amount: selectedPack.totalAmount,
        currency: paymentCurrency,
        method: paymentData.method,
        metadata: JSON.stringify({
          packPurchaseId: purchase.id,
          packId: selectedPack.id,
          billingCountry: paymentData.country || null,
          cryptoAsset:
            paymentData.method === "crypto" ? paymentData.cryptoAsset : null,
          cryptoNetwork: paymentData.method === "crypto" ? paymentData.cryptoNetwork : null,
          ...(paymentData.method === "crypto"
            ? {
                walletAddress: paymentData.walletAddress,
              }
            : {}),
        }),
      });

      if (payment.method === "card" && payment.clientSecret) {
        setStripePayment(payment);
        setPaymentFormOpen(false);
        setSelectedPack(null);
        return;
      }

      if (payment.method === "crypto") {
        setPendingPayment(payment);
        toast({
          title: t("investmentPacks.purchaseInitiatedTitle", "Paiement initié"),
          description: t(
            "transactions.instructionsCryptoText",
            "Envoyez {{amount}} {{currency}} vers l'adresse suivante puis indiquez le hash de transaction.",
            { amount: payment.amount, currency: payment.currency }
          ),
          variant: "info",
        });
        await fetchPacks();
        setPaymentFormOpen(false);
        setSelectedPack(null);
        return;
      }

      if (payment.status === "requires_action") {
        setPendingPayment(payment);
        toast({
          title: t("investmentPacks.purchaseInitiatedTitle", "Paiement initié"),
          description: t(
            "investmentPacks.purchaseManagerApprovalDescription",
            "Votre paiement a été initié. Votre manager recevra un code de confirmation et devra approuver votre paiement. Les fonds seront convertis en crypto et envoyés au wallet de la plateforme."
          ),
          variant: "info",
        });
        await fetchPacks();
        setPaymentFormOpen(false);
        setSelectedPack(null);
        return;
      }

      // Si le paiement est déjà confirmé (ne devrait pas arriver normalement)
      toast({
        title: t("investmentPacks.purchaseInitiatedTitle", "Paiement initié"),
        description: t(
          "investmentPacks.purchaseInitiatedDescription",
          "Votre paiement a été initié. Vous recevrez une confirmation par email."
        ),
        variant: "success",
      });

      await fetchPacks();
      setPaymentFormOpen(false);
      setSelectedPack(null);
    } catch (err) {
      console.error("Erreur lors de l'achat du pack:", err);
      const errorMessage = getPaymentErrorMessage(err);

      toast({
        title: t("investmentPacks.errorTitle"),
        description: errorMessage,
        variant: "destructive",
      });
      throw err; // Re-throw pour que le formulaire puisse gérer l'erreur
    } finally {
      setPurchasing(false);
    }
  };

  // Validation carte gérée par Stripe

  const handleConfirmPayment = async () => {
    // Pour les paiements crypto, pas besoin de confirmationValue
    if (!pendingPayment) {
      return;
    }

    if (pendingPayment.method === "card" && !confirmationValue.trim()) {
      toast({
        title: t("transactions.confirmationRequiredTitle", "Code requis"),
        description: t(
          "transactions.confirmationRequiredCardDescription",
          "Veuillez entrer le code de vérification."
        ),
        variant: "destructive",
      });
      return;
    }

    setConfirming(true);
    try {
      // Pour les paiements crypto, on envoie une chaîne vide (le backend génère le code)
      const payment = await paymentService.confirmPayment(pendingPayment.id, {
        confirmationValue: pendingPayment.method === "crypto" ? "" : confirmationValue.trim(),
      });

      if (payment.status === "confirmed") {
        toast({
          title: t("investmentPacks.purchaseSuccessTitle", "Paiement confirmé"),
          description: t(
            "investmentPacks.purchaseSuccessDescription",
            "Votre paiement a été confirmé avec succès. Votre compte a été crédité."
          ),
          variant: "success",
        });
      } else if (payment.status === "requires_action") {
        toast({
          title: t(
            "investmentPacks.cryptoPaymentSubmittedTitle",
            "Paiement en attente"
          ),
          description: t(
            "investmentPacks.cryptoPaymentSubmittedDescription",
            "Votre paiement a été confirmé. Un code unique a été envoyé à votre manager pour validation. Votre compte sera crédité après approbation."
          ),
          variant: "info",
        });
      }

      await fetchPacks();
      setPendingPayment(null);
      setConfirmationValue("");
      setSelectedPack(null);
    } catch (err) {
      console.error("Erreur lors de la confirmation du paiement:", err);
      let errorMessage: string;

      if (typeof err === "string") {
        errorMessage = err;
      } else if (err instanceof Error) {
        errorMessage = err.message;
      } else {
        errorMessage = t("transactions.confirmPaymentErrorDescription", "Impossible de confirmer le paiement");
      }

      toast({
        title: t("investmentPacks.errorTitle", "Erreur"),
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setConfirming(false);
    }
  };

  const handleStripeSuccess = async (paymentIntentId: string) => {
    if (!stripePayment) return;
    try {
      const payment = await paymentService.confirmPayment(stripePayment.id, {
        confirmationValue: paymentIntentId,
      });
      setStripePayment(null);
      if (payment.status === "confirmed") {
        toast({
          title: t("investmentPacks.purchaseSuccessTitle", "Achat confirmé"),
          description: t(
            "investmentPacks.purchaseSuccessDescription",
            "Votre paiement a été confirmé et votre pack a été activé."
          ),
          variant: "success",
        });
        await fetchPacks();
      }
    } catch (error) {
      toast({
        title: t("investmentPacks.purchaseErrorTitle", "Erreur"),
        description:
          typeof error === "string"
            ? error
            : t("investmentPacks.purchaseErrorDescription"),
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-100 p-4 dark:bg-slate-950">
        <div className="mx-auto max-w-7xl">
          <div className="mb-8">
            <Skeleton className="h-10 w-64 mb-2" />
            <Skeleton className="h-6 w-96" />
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-96 rounded-2xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 p-4 dark:bg-slate-950">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 dark:text-white mb-2">
            {t("investmentPacks.title")}
          </h1>
          <p className="text-slate-600 dark:text-white/70">{t("investmentPacks.subtitle")}</p>
        </div>

        {packs.length === 0 ? (
          <div className="rounded-2xl border border-white/5 bg-gradient-to-br from-slate-900/60 to-slate-800/40 p-12 text-center">
            <p className="text-lg text-slate-600 dark:text-white/70">
              {t("investmentPacks.empty")}
            </p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {packs.map((pack) => {
              const displayName = pickPackName(pack, i18n.language);
              const displayDescription = pickPackDescription(
                pack,
                i18n.language
              );

              const isSoldOut =
                pack.maxPurchases !== null &&
                pack.currentPurchases >= pack.maxPurchases;

              const bgGradient =
                pack.primaryColor && pack.secondaryColor
                  ? `linear-gradient(135deg, ${pack.primaryColor} 0%, ${pack.secondaryColor} 100%)`
                  : pack.primaryColor
                  ? pack.primaryColor
                  : pack.secondaryColor
                  ? pack.secondaryColor
                  : "linear-gradient(135deg, rgb(51 65 85 / 0.5) 0%, rgb(30 41 59 / 0.3) 100%)";

              return (
                <div
                  key={pack.id}
                  className="group relative rounded-2xl border border-white/25 backdrop-blur-sm p-6 shadow-lg shadow-slate-300/40 transition-all duration-500 ease-out hover:scale-[1.02] hover:shadow-2xl hover:shadow-slate-400/50 hover:-translate-y-1 overflow-hidden dark:border-white/10 dark:shadow-black/20 dark:hover:shadow-black/40"
                  style={{
                    background: bgGradient,
                  }}>
                  <div className="absolute inset-0 bg-gradient-to-br from-white/0 via-white/0 to-white/0 group-hover:from-white/5 group-hover:via-white/5 group-hover:to-white/0 transition-all duration-500 pointer-events-none" />
                  <div className="relative z-10">
                    <div className="mb-4">
                      <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
                        {displayName}
                      </h3>
                      {displayDescription && (
                        <p className="text-sm text-slate-600 dark:text-white/70 line-clamp-3">
                          {displayDescription}
                        </p>
                      )}
                    </div>

                    <div className="mb-4 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-500 dark:text-white/60">
                          {t("investmentPacks.totalAmount")}
                        </span>
                        <span className="text-lg font-semibold text-slate-900 dark:text-white">
                          ${pack.totalAmount.toLocaleString()}
                        </span>
                      </div>
                      {pack.maxPurchases !== null && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-500 dark:text-white/60">
                            {t("investmentPacks.availability")}
                          </span>
                          <span className="text-sm font-medium text-slate-700 dark:text-white/80">
                            {pack.maxPurchases - pack.currentPurchases} /{" "}
                            {pack.maxPurchases}
                          </span>
                        </div>
                      )}
                    </div>

                    {pack.benefits && pack.benefits.length > 0 && (
                      <div className="mb-4 rounded-lg border border-white/5 bg-white/5 p-3">
                        <ul className="space-y-2">
                          {pack.benefits.map((benefit) => {
                            const benefitText = pickBenefitText(
                              benefit,
                              i18n.language
                            );
                            return (
                              <li
                                key={benefit.id}
                                className="flex items-start gap-2 text-sm text-slate-700 dark:text-white/80">
                                <div className="mt-0.5 flex-shrink-0">
                                  <div className="h-5 w-5 rounded-full bg-emerald-500 flex items-center justify-center">
                                    <svg
                                      className="h-3 w-3 text-white"
                                      fill="none"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor">
                                      <path d="M5 13l4 4L19 7" />
                                    </svg>
                                  </div>
                                </div>
                                <span className="flex-1">{benefitText}</span>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}

                    <Button
                      className="w-full"
                      disabled={isSoldOut}
                      onClick={() => handleOpenPurchase(pack)}>
                      {isSoldOut
                        ? t("investmentPacks.soldOut")
                        : !isAuthenticated
                        ? t("investmentPacks.buyNowButton")
                        : t("investmentPacks.purchaseButton")}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {totalPacks > packsPerPage && (
          <div className="mt-8 border-t border-white/10 pt-3">
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              totalItems={totalPacks}
              itemsPerPage={packsPerPage}
              onPageChange={setPage}
              isLoading={isLoading}
              ariaLabelPrevious={t("investmentPacks.pagination.previous")}
              ariaLabelNext={t("investmentPacks.pagination.next")}
            />
          </div>
        )}

        {selectedPack && (
          <PaymentFormModal
            open={paymentFormOpen}
            onClose={() => {
              setPaymentFormOpen(false);
              setSelectedPack(null);
            }}
            onConfirm={handlePaymentConfirm}
            amount={selectedPack.totalAmount}
            packName={pickPackName(selectedPack, i18n.language)}
            isProcessing={purchasing}
          />
        )}

        {/* Modal de confirmation du paiement */}
        <Dialog
          open={!!pendingPayment}
          onOpenChange={(open) => {
            if (!open) {
              setPendingPayment(null);
              setConfirmationValue("");
            }
          }}>
          <DialogContent className="border-slate-200 bg-white text-slate-900 dark:border-white/10 dark:bg-slate-950 dark:text-white">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">
                {t("payment.confirm.title", "Confirmer le paiement")}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {pendingPayment?.method === "crypto" ? (
                <div className="space-y-3 text-slate-600 dark:text-white/70">
                  <p>
                    {t(
                      "transactions.instructionsCryptoText",
                      "Envoyez {{amount}} {{currency}} vers l'adresse suivante puis indiquez le hash de transaction.",
                      {
                        amount: pendingPayment.amount,
                        currency: pendingPayment.currency,
                      }
                    )}
                  </p>
                  {pendingPayment.metadata && (() => {
                    try {
                      const parsed = JSON.parse(pendingPayment.metadata);
                      const network =
                        typeof parsed?.cryptoNetwork === "string"
                          ? parsed.cryptoNetwork.toUpperCase()
                          : null;
                      const asset =
                        typeof parsed?.cryptoAsset === "string"
                          ? parsed.cryptoAsset.toUpperCase()
                          : null;
                      const label =
                        asset === "ETH" || network === "ERC20"
                          ? t("payment.form.cryptoNetworkEthLabel", "ERC20 (ETH)")
                          : asset === "BNB" || network === "BSC"
                          ? t("payment.form.cryptoNetworkBnbLabel", "BSC (BEP20)")
                          : asset === "BTC" || network === "BTC"
                          ? t("payment.form.cryptoNetworkBtcLabel", "BTC (mainnet)")
                          : network;
                      if (!label) return null;
                      return (
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-white/60">
                            <span>
                              {t("payment.form.cryptoNetworkLabel", "Réseau")}:
                            </span>
                            <span className="rounded-full border border-white/10 bg-white/10 px-2 py-0.5 text-[10px] uppercase text-slate-600 dark:text-white/70">
                              {label}
                            </span>
                          </div>
                          <p className="text-xs text-amber-300/90">
                            {t(
                              "payment.form.cryptoNetworkWarning",
                              "⚠️ Sélectionnez exactement le même réseau que celui de votre wallet (ex: Binance), sinon les fonds peuvent être perdus."
                            )}
                          </p>
                        </div>
                      );
                    } catch {
                      return null;
                    }
                  })()}
                  {pendingPayment.walletAddress && (
                    <div className="rounded-lg border border-white/10 bg-white/5 p-3">
                      <p className="text-xs text-slate-500 dark:text-white/60">
                        {t("transactions.instructionsAddress", "Adresse")}
                      </p>
                      <div className="mt-2 flex items-center gap-2">
                        <p className="flex-1 break-all text-sm text-slate-900 dark:text-white">
                          {pendingPayment.walletAddress}
                        </p>
                        <Button
                          type="button"
                          variant="ghost"
                          aria-label={t("accounts.copyButton", "Copier")}
                          className="h-8 w-8 shrink-0 rounded-md border border-white/10 bg-white/5 p-0 text-slate-700 dark:text-white/80 hover:bg-white/10 hover:text-white"
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(
                                pendingPayment.walletAddress || ""
                              );
                              toast({
                                title: t("accounts.copySuccessTitle", "Copié"),
                                description: t(
                                  "accounts.copySuccessDescription",
                                  "Adresse copiée."
                                ),
                              });
                            } catch {
                              toast({
                                title: t("accounts.copyErrorTitle", "Erreur"),
                                description: t(
                                  "accounts.copyErrorDescription",
                                  "Impossible de copier l'adresse."
                                ),
                                variant: "destructive",
                              });
                            }
                          }}>
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                      <img
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(
                          pendingPayment.walletAddress ?? ""
                        )}`}
                        alt={t("transactions.instructionsAddress", "Adresse")}
                        className="mt-3 h-24 w-24 rounded-md bg-white p-1"
                      />
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-slate-600 dark:text-white/70">
                  {t(
                    "payment.confirm.description",
                    "Un code de vérification a été généré. Veuillez l'entrer ci-dessous pour confirmer votre paiement."
                  )}
                </p>
              )}
              {pendingPayment?.method === "card" && (
                <div>
                  <Label className="text-slate-600 dark:text-white/70 mb-2 block">
                    {t("payment.confirm.codeLabel", "Code de vérification")}
                  </Label>
                  <Input
                    type="text"
                    placeholder={t(
                      "payment.confirm.codePlaceholder",
                      "Entrez le code de vérification"
                    )}
                    value={confirmationValue}
                    onChange={(e) => setConfirmationValue(e.target.value)}
                    className="border-slate-200 bg-white text-slate-900 dark:bg-white/5 dark:text-white dark:border-white/10"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && confirmationValue.trim()) {
                        handleConfirmPayment();
                      }
                    }}
                  />
                </div>
              )}
              {pendingPayment?.method === "crypto" && (
                <div className="rounded-lg border border-cyan-500/20 bg-cyan-500/10 p-4">
                  <p className="text-sm text-cyan-400 mb-2">
                    {t(
                      "payment.confirm.cryptoInstructions",
                      "Confirmez votre paiement. Un code unique sera envoyé à votre manager pour validation."
                    )}
                  </p>
                </div>
              )}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setPendingPayment(null);
                    setConfirmationValue("");
                  }}
                  disabled={confirming}
                  className="flex-1 text-slate-700 dark:text-white">
                  {t("payment.confirm.cancel", "Annuler")}
                </Button>
                <Button
                  type="button"
                  onClick={handleConfirmPayment}
                  disabled={confirming || (pendingPayment?.method === "card" && !confirmationValue.trim())}
                  className="flex-1 bg-gradient-to-r from-cyan-400 to-blue-500 text-white hover:from-cyan-500 hover:to-blue-600">
                  {confirming
                    ? t("payment.confirm.processing", "Traitement...")
                    : t("payment.confirm.confirm", "Confirmer")}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {stripePayment?.clientSecret && (
          <StripePaymentModal
            open={!!stripePayment}
            clientSecret={stripePayment.clientSecret}
            amount={stripePayment.amount}
            currency={stripePayment.currency}
          metadata={stripePayment.metadata}
            onClose={() => setStripePayment(null)}
            onSuccess={handleStripeSuccess}
          />
        )}
      </div>
    </div>
  );
};

export default InvestmentPacksPage;
